import type { Object3D } from 'three';
import { Mesh, Vector3 } from 'three';

import type { CameraHelperManager } from '../../../three-helpers/camera';
import type { HighlightManager } from '../../../three-helpers/highlight';
import type { LightHelperManager } from '../../../three-helpers/light';
import type {
	CameraManager,
	CutoutManager,
	DebugManager,
	EventManager,
	LightingManager,
	SceneManager
} from '../../managers';
import type { KitchenDebugData } from '../../types';
import { calculateSceneBoundsMetrics } from '../functions';
import { createRotationAxisArcsCfn } from './functions/create-rotation-axis-arcs.cfn';
import { setKitchenDebugDataCfn } from './functions/kitchen-debug-data.cfn';
import type { SceneCameraDelegate } from './scene-camera-delegate.class';

export type SceneDebugDelegateConfig = {
	debugManager: DebugManager;
	lightingManager: LightingManager;
	highlightManager: HighlightManager;
	lightHelperManager: LightHelperManager;
	cameraHelperManager: CameraHelperManager;
	cameraManager: CameraManager;
	sceneManager: SceneManager;
	cutoutManager: CutoutManager;
	eventManager: EventManager;
	getCameraDelegate: () => SceneCameraDelegate | null;
};

/**
 * Delegate that owns debug visualization and inspection operations.
 * Extracted from ThreeSceneEngine to reduce file size.
 */
export class SceneDebugDelegate {
	#config: SceneDebugDelegateConfig;

	constructor (config: SceneDebugDelegateConfig) {
		this.#config = config;
	}

	/**
	 * Set debug mode with optional per-feature options
	 */
	setDebugMode (enabled: boolean, options?: { cutouts?: boolean; lights?: boolean; camera?: boolean; bounds?: boolean }): void {
		if (this.#config.cameraManager) {
			this.#config.cameraManager.debugMode = enabled;
		}

		if (this.#config.eventManager) {
			this.#config.eventManager.debugMode = enabled;
		}

		// Bounds: debug manager circles, spheres, wireframes
		const showBounds = enabled && (options?.bounds !== false);
		if (this.#config.debugManager) {
			this.#config.debugManager.setVisible(showBounds);

			const cameraDelegate = this.#config.getCameraDelegate();
			if (showBounds && cameraDelegate?.lastSceneBounds) {
				this.createDebugSceneVisualization();
			}

			if (showBounds) {
				this.createSceneObjectFaceDebug();
			}
		}

		// Lights: light helper gizmos + highlight debug
		const showLights = enabled && (options?.lights !== false);
		this.setDebugLights(showLights);

		if (this.#config.highlightManager) {
			this.#config.highlightManager.setDebugMode(showLights);
		}

		// Camera: camera helper gizmos
		const showCamera = enabled && (options?.camera !== false);
		this.setDebugCamera(showCamera);

		// Cutouts: debug cutout boxes
		const showCutouts = enabled && (options?.cutouts !== false);
		if (this.#config.cutoutManager) {
			this.#config.cutoutManager.setDebugMode(showCutouts);
		}
	}

	/**
	 * Toggle debug visualization of cutouts specifically
	 * Shows semi-transparent red boxes where cutouts are being applied
	 */
	setDebugCutouts (enabled: boolean): void {
		if (this.#config.cutoutManager) {
			this.#config.cutoutManager.setDebugMode(enabled);
		}
	}

	/**
	 * Get debug cutouts state
	 */
	get debugCutoutsEnabled (): boolean {
		return this.#config.cutoutManager?.debugMode ?? false;
	}

	/**
	 * Toggle debug visualization of light helpers specifically
	 */
	setDebugLights (enabled: boolean): void {
		if (this.#config.lightHelperManager) {
			this.#config.lightHelperManager.setVisible(enabled);

			if (enabled) {
				this.#config.lightHelperManager.setDebugManager(this.#config.debugManager ?? null);
				this.#config.lightHelperManager.enableInteraction();
				this.#config.lightHelperManager.setOnInteractionStart(() => {
					if (this.#config.cameraManager?.controls) {
						this.#config.cameraManager.controls.enabled = false;
					}
				});
				this.#config.lightHelperManager.setOnInteractionEnd(() => {
					if (this.#config.cameraManager?.controls) {
						this.#config.cameraManager.controls.enabled = true;
					}
				});
			} else {
				this.#config.lightHelperManager.setDebugManager(null);
				this.#config.lightHelperManager.disableInteraction();
			}
		}
	}

	/**
	 * Toggle debug visualization of camera helpers specifically
	 */
	setDebugCamera (enabled: boolean): void {
		if (this.#config.cameraHelperManager) {
			this.#config.cameraHelperManager.setVisible(enabled);
		}
	}

	/**
	 * Create debug visualization for the scene (circles, spheres, etc.)
	 * Called when debug mode is enabled
	 */
	createDebugSceneVisualization (): void {
		const cameraDelegate = this.#config.getCameraDelegate();
		const debugBounds = cameraDelegate?.lastSceneBounds;
		if (!this.#config.debugManager || !debugBounds) return;

		const { min, max } = debugBounds;
		const metrics = calculateSceneBoundsMetrics(min, max);

		// Kitchen circle (green) - shows the main scene bounds
		this.#config.debugManager.addCircle({
			id: 'kitchen-circle',
			center: new Vector3(metrics.centerX, metrics.kitchenCircleY, metrics.centerZ),
			radius: metrics.kitchenCircleRadius,
			color: 0x00ff00 // Green
		});

		// Highlight circle (yellow) - shows the highlight light path
		this.#config.debugManager.addCircle({
			id: 'highlight-circle',
			center: new Vector3(metrics.centerX, metrics.highlightCircleY, metrics.centerZ),
			radius: metrics.highlightCircleRadius,
			color: 0xffff00 // Yellow
		});

		// Center sphere (green) - marks the center of the scene
		this.#config.debugManager.addSphere({
			id: 'scene-center',
			position: new Vector3(metrics.centerX, metrics.kitchenCircleY, metrics.centerZ),
			radius: 1,
			color: 0x00ff00,
			label: 'Scene Center'
		});
	}

	/**
	 * Set kitchen-specific debug visualization data
	 */
	setKitchenDebugData (data: KitchenDebugData): void {
		if (!this.#config.debugManager) return;

		setKitchenDebugDataCfn({
			data,
			debugManager: this.#config.debugManager,
			lightingManager: this.#config.lightingManager ?? null,
			highlightManager: this.#config.highlightManager ?? null,
			cameraDelegate: this.#config.getCameraDelegate()
		});
	}

	/**
	 * Create face debug visualization for scene objects (surrounds, countertops, corners, appliances)
	 * Shows 2" spheres at face centers with 18" perpendicular lines
	 */
	async createSceneObjectFaceDebug (): Promise<void> {
		if (!this.#config.debugManager || !this.#config.sceneManager?.scene) return;

		const { Box3, Vector3: Vec3 } = await import('three');
		const { scene } = this.#config.sceneManager;

		// Find all highlightable objects
		const objectTypes = ['surround', 'countertop', 'corner', 'appliance', 'panel'];
		let objectCount = 0;

		scene.traverse((object) => {
			const type = object.userData?.type;
			if (!type || !objectTypes.some((t) => type.toLowerCase().includes(t))) return;
			if (!object.visible) return;

			// Calculate bounding box
			const bbox = new Box3().setFromObject(object);
			if (bbox.isEmpty()) return;

			const center = bbox.getCenter(new Vec3());
			const size = bbox.getSize(new Vec3());
			const id = object.userData?.id || object.name || `object-${objectCount}`;

			// Front face (max Z)
			const frontCenter = new Vec3(center.x, center.y, bbox.max.z);
			const frontNormal = new Vec3(0, 0, 1);
			const frontEnd = frontCenter.clone().add(frontNormal.multiplyScalar(18));

			this.#config.debugManager!.addSphere({
				id: `${id}-front-face`,
				position: frontCenter.clone(),
				radius: 1, // 2" diameter = 1" radius
				color: 0xffffcc, // Light yellow
				label: `${id} Front`
			});
			this.#config.debugManager!.addLine({
				id: `${id}-front-vector`,
				start: frontCenter.clone(),
				end: frontEnd,
				color: 0xffffcc
			});

			// Top face (max Y)
			const topCenter = new Vec3(center.x, bbox.max.y, center.z);
			const topNormal = new Vec3(0, 1, 0);
			const topEnd = topCenter.clone().add(topNormal.multiplyScalar(18));

			this.#config.debugManager!.addSphere({
				id: `${id}-top-face`,
				position: topCenter.clone(),
				radius: 1,
				color: 0xffffcc,
				label: `${id} Top`
			});
			this.#config.debugManager!.addLine({
				id: `${id}-top-vector`,
				start: topCenter.clone(),
				end: topEnd,
				color: 0xffffcc
			});

			objectCount++;
		});

		// console.log(`SceneDebugDelegate: Created face debug visualization for ${objectCount} objects`);
	}

	/**
	 * Create colored arrow-arc visualizations for appliances that have non-zero
	 * modelRotation values, showing which axis is rotated and in which direction.
	 * X -> red arc (YZ plane), Y -> green arc (XZ plane), Z -> blue arc (XY plane).
	 * Only drawn when debug mode is active.
	 */
	createRotationAxisArcs (): void {
		if (!this.#config.debugManager || !this.#config.sceneManager) return;

		createRotationAxisArcsCfn({
			debugManager: this.#config.debugManager,
			sceneManager: this.#config.sceneManager
		});
	}

	/**
	 * Debug helper: Check visibility state of objects in the scene after animations complete.
	 * Outputs a summary to diagnose rendering issues.
	 */
	debugCheckObjectOpacity (container: Object3D): void {
		let totalMeshes = 0;
		let visibleCount = 0;
		let invisibleOpacity = 0;
		let hiddenVisibility = 0;
		let zeroScale = 0;
		let minX = Infinity,
			minY = Infinity,
			minZ = Infinity;
		let maxX = -Infinity,
			maxY = -Infinity,
			maxZ = -Infinity;

		container.traverse((child) => {
			if (child instanceof Mesh && child.material) {
				const materials = Array.isArray(child.material) ? child.material : [child.material];
				for (const material of materials) {
					totalMeshes++;
					const isOpaque = material.opacity > 0.5;
					const isVisible = child.visible;
					const hasScale = child.scale.x > 0.01 && child.scale.y > 0.01 && child.scale.z > 0.01;

					if (isOpaque && isVisible && hasScale) {
						visibleCount++;
						// Track bounds of visible meshes
						const worldPos = new Vector3();
						child.getWorldPosition(worldPos);
						if (isNaN(worldPos.x) || isNaN(worldPos.y) || isNaN(worldPos.z)) {
							const parentPos = child.parent?.position;
							console.warn(`[NaN POSITION] mesh="${child.name}" localPos=(${child.position.x}, ${child.position.y}, ${child.position.z}) parent="${child.parent?.name}" parentPos=(${parentPos?.x}, ${parentPos?.y}, ${parentPos?.z})`);
						} else {
							minX = Math.min(minX, worldPos.x);
							minY = Math.min(minY, worldPos.y);
							minZ = Math.min(minZ, worldPos.z);
							maxX = Math.max(maxX, worldPos.x);
							maxY = Math.max(maxY, worldPos.y);
							maxZ = Math.max(maxZ, worldPos.z);
						}
					}
					if (!isOpaque) {
						// console.warn(`[LOW OPACITY] mesh="${child.name}" opacity=${material.opacity.toFixed(2)}`);
						invisibleOpacity++;
					}
					if (!isVisible) hiddenVisibility++;
					if (!hasScale) zeroScale++;
				}
			}
		});

		// Check if container is in scene
		const scene = this.#config.sceneManager?.scene;
		let inScene = false;
		if (scene) {
			let current: Object3D | null = container;
			while (current) {
				if (current === scene) {
					inScene = true;
					break;
				}
				current = current.parent;
			}
		}

		// Get camera info
		const cam = this.#config.cameraManager?.camera;
		const camPos = cam ? `(${cam.position.x.toFixed(1)}, ${cam.position.y.toFixed(1)}, ${cam.position.z.toFixed(1)})` : 'no camera';
		const target = this.#config.cameraManager?.controls?.getTarget(new Vector3());
		const camTarget = target ? `(${target.x.toFixed(1)}, ${target.y.toFixed(1)}, ${target.z.toFixed(1)})` : 'no target';

		// console.warn(`[SCENE CHECK] container="${container.name}" inScene=${inScene} children=${container.children.length}`);
		// console.warn(`[MESH CHECK] total=${totalMeshes} fullyVisible=${visibleCount} lowOpacity=${invisibleOpacity} hidden=${hiddenVisibility} zeroScale=${zeroScale}`);
		const boundsValid = isFinite(minX) && isFinite(maxX);
		const boundsStr = boundsValid
			? `min=(${minX.toFixed(1)}, ${minY.toFixed(1)}, ${minZ.toFixed(1)}) max=(${maxX.toFixed(1)}, ${maxY.toFixed(1)}, ${maxZ.toFixed(1)})`
			: 'NO VALID BOUNDS - all visible meshes have NaN positions';
		// console.warn(`[MESH BOUNDS] ${boundsStr}`);
		// console.warn(`[CAMERA] pos=${camPos} target=${camTarget}`);
	}
}
